import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import TemplateRenderer from "./templateRenderer";

const PREVIEW_WIDTH = 794;
const PREVIEW_HEIGHT = 1123;

const SCALE_PRESETS = {
  card: 0.28,
  modal: 0.62,
  sidebar: 0.205,
};

function TemplateLivePreview({
  templateId,
  variant = "card",
  scale,
  borderRadius = 18,
  canvasHeight = PREVIEW_HEIGHT,
  fitWidth = false,
}) {
  const containerRef = useRef(null);
  const previewRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [measuredCanvasHeight, setMeasuredCanvasHeight] = useState(canvasHeight);

  useEffect(() => {
    if (!fitWidth || !containerRef.current) return undefined;

    const node = containerRef.current;
    const updateWidth = () => {
      const nextWidth = node.clientWidth || 0;
      setContainerWidth((currentWidth) =>
        Math.abs(currentWidth - nextWidth) > 1 ? nextWidth : currentWidth,
      );
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    window.addEventListener("resize", updateWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, [fitWidth]);

  useEffect(() => {
    setMeasuredCanvasHeight((currentHeight) =>
      Math.abs(currentHeight - canvasHeight) > 1 ? canvasHeight : currentHeight,
    );
  }, [canvasHeight, templateId]);

  useEffect(() => {
    if (!fitWidth || !previewRef.current) return undefined;

    const previewNode = previewRef.current;

    const measureHeight = () => {
      const pageNode =
        previewNode.querySelector(".cv-page") ||
        previewNode.querySelector(".page") ||
        previewNode.firstElementChild;

      if (!pageNode) return;

      const nextHeight =
        pageNode.scrollHeight ||
        pageNode.offsetHeight ||
        canvasHeight ||
        PREVIEW_HEIGHT;

      setMeasuredCanvasHeight((currentHeight) =>
        Math.abs(currentHeight - nextHeight) > 1 ? nextHeight : currentHeight,
      );
    };

    const frameId = window.requestAnimationFrame(measureHeight);
    const timeoutId = window.setTimeout(measureHeight, 120);

    window.addEventListener("resize", measureHeight);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      window.removeEventListener("resize", measureHeight);
    };
  }, [fitWidth, templateId, canvasHeight]);

  const resolvedScale = useMemo(() => {
    if (fitWidth && containerWidth > 0) {
      return containerWidth / PREVIEW_WIDTH;
    }

    return scale || SCALE_PRESETS[variant] || SCALE_PRESETS.card;
  }, [containerWidth, fitWidth, scale, variant]);

  const resolvedCanvasHeight = fitWidth ? measuredCanvasHeight : canvasHeight;
  const width = fitWidth ? "100%" : PREVIEW_WIDTH * resolvedScale;
  const height = resolvedCanvasHeight * resolvedScale;

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        width,
        height,
        overflow: "hidden",
        borderRadius,
        background: "#ffffff",
        position: "relative",
        contain: "layout paint style",
        contentVisibility: "auto",
      }}
    >
      <div
        ref={previewRef}
        style={{
          width: PREVIEW_WIDTH,
          height: resolvedCanvasHeight,
          transform: `scale(${resolvedScale})`,
          transformOrigin: "top left",
          pointerEvents: "none",
          position: "absolute",
          inset: 0,
          overflow: "hidden",
        }}
      >
        <TemplateRenderer templateId={templateId} />
      </div>
    </div>
  );
}

export default memo(TemplateLivePreview);
